import { Button, Tabs } from 'AWS-UI-Components-React';
import React from 'react';
import { RegionItems } from '../../const/dropdown_elements';
import { getJSClient } from '../../js_client';
import { NewValidationWindow } from './new_validation_window';
import { ViewByTimeFrame } from './view_by_timeframe';
import {
  aggregateByTimeframe,
  aggregateByExecutionIdPrefix,
  aggregateByMarketplace,
  aggregateByRegion,
  getChartByKey,
  getTitle,
  aggregateByChannel,
} from './utils';
import { ViewByMarketplace } from './view_by_marketplace';
import { ViewByRegion } from './view_by_region';
import { ViewByChannel } from './view_by_channel';

export class ValidationTestResultsView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tabContent: [],
      activeTabId: 'all',
    };
  }

  componentDidMount = () => {
    this.client = getJSClient();
    this.initContent();
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.validationTestResults !== this.props.validationTestResults) {
      this.initContent();
    }
  };

  initContent = () => {
    const { validationTestResults } = this.props;
    const hasTestResults = validationTestResults && validationTestResults.length > 0;
    const testResultsContent = hasTestResults ? this.prepareTestResultContent(validationTestResults) : null;
    const tabContent = hasTestResults ? this.getTabs(testResultsContent) : null;

    this.hasTestResults = hasTestResults;

    this.setState({ tabContent });
  };

  prepareTestResultContent = (validationTestResults) => {
    const testResultsByExecutionIdPrefix = aggregateByExecutionIdPrefix(validationTestResults);
    return {
      all: this.getAllTestResultsContent(testResultsByExecutionIdPrefix),
      byMarketplace: this.getByMarketplaceTestResultContent(testResultsByExecutionIdPrefix),
      byRegion: this.getByRegionTestResultContent(testResultsByExecutionIdPrefix),
      byChannel: this.getByChannelTestResultContent(testResultsByExecutionIdPrefix),
    };
  };

  getAllTestResultsContent = (testResultsByExecutionIdPrefix) => {
    return (
      <div>
        {Object.keys(testResultsByExecutionIdPrefix)
          .sort()
          .reverse() // latest runs show up first
          .map((executionIdPrefix) => {
            const testResultsByTimeFrame = aggregateByTimeframe(testResultsByExecutionIdPrefix[executionIdPrefix]);
            const title = getTitle(executionIdPrefix);
            return (
              <ViewByTimeFrame
                key={'TestResultsByTimeFrame' + executionIdPrefix}
                testResultsByTimeFrame={testResultsByTimeFrame}
                title={title}
                chart={getChartByKey(testResultsByTimeFrame)}
                loadValidationTestResults={this.props.loadValidationTestResults}
              />
            );
          })}
      </div>
    );
  };

  getByMarketplaceTestResultContent = (testResultsByExecutionIdPrefix) => {
    const { marketplaceItems } = this.props;

    const marketplaceLabelsById = marketplaceItems.reduce((acc, item) => {
      acc[item.id] = item.label;
      return acc;
    }, {});

    return (
      <div>
        {Object.keys(testResultsByExecutionIdPrefix)
          .sort()
          .reverse() // latest runs show up first
          .map((executionIdPrefix) => {
            const testResultsByMarketplace = aggregateByMarketplace(testResultsByExecutionIdPrefix[executionIdPrefix]);
            const title = getTitle(executionIdPrefix);
            const testResultsByMarketplaceLabel = Object.keys(testResultsByMarketplace).reduce((acc, marketplace) => {
              const key = marketplaceLabelsById[marketplace] || marketplace;
              acc[key] = testResultsByMarketplace[marketplace];
              return acc;
            }, {});
            return (
              <div key={'TestResultsByMarketplaceDiv' + executionIdPrefix}>
                <ViewByMarketplace
                  testResultsByMarketplace={testResultsByMarketplace}
                  title={title}
                  chart={getChartByKey(testResultsByMarketplaceLabel)}
                  loadValidationTestResults={this.props.loadValidationTestResults}
                  marketplaceItems={marketplaceItems}
                  marketplaceLabelsById={marketplaceLabelsById}
                />
                <hr />
              </div>
            );
          })}
      </div>
    );
  };

  getByRegionTestResultContent = (testResultsByExecutionIdPrefix) => {
    const regionLabelsById = RegionItems.reduce((acc, item) => {
      const key = item.id === 'fe' ? 'ap' : item.id;
      acc[key] = item.label;
      return acc;
    }, {});

    return (
      <div>
        {Object.keys(testResultsByExecutionIdPrefix)
          .sort()
          .reverse() // latest runs show up first
          .map((executionIdPrefix) => {
            const testResultsByRegion = aggregateByRegion(testResultsByExecutionIdPrefix[executionIdPrefix]);
            const title = getTitle(executionIdPrefix);

            const testResultsByRegionLabel = Object.keys(testResultsByRegion).reduce((acc, region) => {
              const regionKey = regionLabelsById[region] || region;
              acc[regionKey] = testResultsByRegion[region];
              return acc;
            }, {});

            return (
              <div key={'TestResultsByRegionDiv' + executionIdPrefix}>
                <ViewByRegion
                  testResultsByRegion={testResultsByRegion}
                  title={title}
                  chart={getChartByKey(testResultsByRegionLabel)}
                  loadValidationTestResults={this.props.loadValidationTestResults}
                  regionLabelsById={regionLabelsById}
                />
                <hr />
              </div>
            );
          })}
      </div>
    );
  };

  getByChannelTestResultContent = (testResultsByExecutionIdPrefix) => {
    return (
      <div>
        {Object.keys(testResultsByExecutionIdPrefix)
          .sort()
          .reverse() // latest runs show up first
          .map((executionIdPrefix) => {
            const testResultsByChannel = aggregateByChannel(testResultsByExecutionIdPrefix[executionIdPrefix]);
            const title = getTitle(executionIdPrefix);

            return (
              <div key={'TestResultsByChannelDiv' + executionIdPrefix}>
                <ViewByChannel
                  testResultsByChannel={testResultsByChannel}
                  title={title}
                  chart={getChartByKey(testResultsByChannel)}
                  loadValidationTestResults={this.props.loadValidationTestResults}
                />
                <hr />
              </div>
            );
          })}
      </div>
    );
  };

  getTabs = (testResultsContent) => {
    return [
      {
        label: 'All',
        id: 'all',
        content: testResultsContent.all,
      },
      {
        label: 'By Marketplace',
        id: 'byMarketplace',
        content: testResultsContent.byMarketplace,
      },
      {
        label: 'By Region',
        id: 'byRegion',
        content: testResultsContent.byRegion,
      },
      {
        label: 'By Channel',
        id: 'byChannel',
        content: testResultsContent.byChannel,
      },
    ];
  };

  showNewValidationsWindow = () => {
    this.setState({ showNewValidationsWindow: true });
  };

  hideNewValidationsWindow = () => {
    this.setState({ showNewValidationsWindow: false });
  };

  newValidationsWindow = () => {
    return <NewValidationWindow onCancel={this.hideNewValidationsWindow} simulation={this.props.simulation} />;
  };

  onTabChange = (event) => {
    this.setState({ activeTabId: event.detail.activeTabId });
  };

  render() {
    const { tabContent, activeTabId } = this.state;
    return (
      <div className="awsui-util-container">
        {this.state.showNewValidationsWindow && this.newValidationsWindow()}
        <div className="awsui-util-container-header">
          <div className="awsui-util-action-stripe">
            <div className="awsui-util-action-stripe-title">
              <h2>Validation Test Results</h2>
            </div>
            <div className="awsui-util-action-stripe-group">
              <Button text="Run Validation Tests" onClick={this.showNewValidationsWindow} />
            </div>
          </div>
        </div>
        {!this.hasTestResults ? (
          <div>No test results found.</div>
        ) : (
          <div>
            <Tabs tabs={tabContent} activeTabId={activeTabId} onChange={this.onTabChange} />
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/simulator/validation_test_results/validation_test_results_view.jsx